import {Record} from 'immutable';
import {JSONSchema7} from "json-schema";
import * as uuid from "uuid";
import {Aggregate, AggregateType} from "../../core/model/Aggregate";
import {Either, isFailure, Success, validate} from "../../core/validation/either";
import {hasKey, isJSON} from "../../core/validation/predicates";
import {UserId} from "../../User/model/UserInfo";
import {parse} from "../service/json/json-comment-wrapper";
import {PlayCommandHandlerRegistry, PlayCommandRegistry} from "../service/playshot/diff/commands/command-definitions";
import {PlayApplyRulesRegistry, PlayEventRegistry} from "../service/playshot/diff/events/event-definitions";
import {
  PlayInformationRegistry,
  PlayQueryRegistry,
  PlayResolverRegistry,
  PlayViewRegistry
} from "../service/playshot/diff/information/information-definitions";
import {PlayPageRegistry} from "../service/playshot/diff/pages/page-definitions";
import {Persona} from "../service/playshot/persona-types";
import {PlayEventPolicyRegistry} from "../service/playshot/diff/policies/policy-definitions";

export const AGGREGATE_TYPE = 'Playshot';

export type PlayshotId = string;
export type PlayshotError = string;

export interface CodyPlayConfig {
  appName: string,
  defaultService: string,
  layout: 'prototype' | 'task-based-ui',
  theme: object,
  personas: Persona[],
  pages: PlayPageRegistry,
  views: PlayViewRegistry,
  commands: PlayCommandRegistry,
  commandHandlers: PlayCommandHandlerRegistry,
  events: PlayEventRegistry,
  eventReducers: PlayApplyRulesRegistry,
  queries: PlayQueryRegistry,
  resolvers: PlayResolverRegistry,
  types: PlayInformationRegistry,
  eventPolicies: PlayEventPolicyRegistry,
  /*
  aggregates: PlayAggregateRegistry,

   */
  definitions: { [definitionId: string]: JSONSchema7 };
}

export interface PlayshotProps {
  uid: PlayshotId;
  aggregateType: AggregateType;
  playshotId: string;
  boardId: string;
  name: string;
  savedAt: string;
  savedBy: UserId;
  playConfig: CodyPlayConfig;
  playData: object;
}

export const defaultPlayshotProps: PlayshotProps = {
  uid: '',
  aggregateType: AGGREGATE_TYPE,
  playshotId: '',
  boardId: '',
  name: '',
  savedAt: '',
  savedBy: '',
  playConfig: {
    appName: 'Cody Play',
    defaultService: 'App',
    pages: {},
    views: {},
    commands: {},
    commandHandlers: {},
    events: {},
    eventReducers: {},
    queries: {},
    resolvers: {},
    eventPolicies: {},
    types: {},
    layout: "prototype",
    theme: {},
    personas: [],
    definitions: {}
  },
  playData: {},
};

export const createPlayshotFromServerData = (data: any): Either<PlayshotError, Playshot> => {
  const validatedData: Either<PlayshotError, any> = validate(data,
    [hasKey('playshotId'), "playshotId missing"],
    [hasKey('name'), "boardId missing"],
    [hasKey('savedAt'), "boardId missing"],
    [hasKey('savedBy'), "boardId missing"],
    [hasKey('playConfig'), "playConfig missing"],
    [isJSON('playConfig'), "playConfig is not a valid JSON string"],
    [isJSON('playData'), "playData is not a valdi JSON string"],
  );

  if (isFailure(validatedData)) {
    return validatedData;
  }

  return Success(new Playshot({
    ...defaultPlayshotProps,
    ...data,
    uid: data.playshotId,
    playConfig: parse(data.playConfig),
    playData: JSON.parse(data.playData),
  }));
};

export class Playshot extends Record(defaultPlayshotProps) implements PlayshotProps, Aggregate {
  public constructor(data: Partial<PlayshotProps>) {
    if(data.playshotId !== '') {
      data.uid = data.playshotId;
    }

    if (data.uid === '' && data.playshotId === '') {
      data.uid = uuid.v4()
      data.playshotId = data.uid;
    }



    if(data.savedAt === '') {
      data.savedAt = (new Date()).toISOString();
    }

    super(data);
  }

  // @TODO add use case methods to change state
}
